<template>
  <auth-layout pageClass="login-page">
    <div class="row d-flex justify-content-center align-items-center">
      <div class="col-12 col-xl-4">
        <ValidationObserver v-slot="{ handleSubmit }">
          <form class="label-control" @submit.prevent="handleSubmit(signIn)">
            <card>
              <div class="card-header" slot="header">
                <h3
                  class="card-title text-center"
                  style="color: #d9d9d9; font-weight: bold"
                >
                  Login
                </h3>
              </div>
              <div v-if="sessionMessage" class="alert alert-warning mb-3">
                {{ sessionMessage }}
              </div>
              <div>
                <div class="login-fields-container">
                  <ValidationProvider
                    name="Email"
                    rules="required|email"
                    tag="div"
                    class="mb-3"
                    v-slot="{ errors }"
                  >
                    <label
                      >Email Address
                      <el-input
                        v-model="email"
                        type="email"
                        class="small-select login-inputs-background"
                      ></el-input>
                    </label>
                    <div
                      v-if="errors.length"
                      class="text-danger"
                      style="font-size: 0.875em"
                    >
                      {{ errors[0] }}
                    </div>
                  </ValidationProvider>
                  <ValidationProvider
                    name="Password"
                    rules="required|min:5"
                    tag="div"
                    class="mb-3"
                    v-slot="{ errors }"
                  >
                    <label
                      >Password
                      <div class="position-relative">
                        <el-input
                          v-model="password"
                          :type="passwordInputType"
                          class="small-select login-inputs-background"
                        ></el-input>
                        <i
                          :class="passwordVisible ? 'fa-solid fa-eye-slash' : 'fa-solid fa-eye'"
                          @click="togglePasswordVisibility"
                          class="position-absolute"
                          style="
                            top: 50%;
                            right: 12px;
                            transform: translateY(-50%);
                            cursor: pointer;
                            color: #666;
                          "
                          aria-hidden="true"
                        ></i>
                      </div>
                    </label>
                    <div
                      v-if="errors.length"
                      class="text-danger"
                      style="font-size: 0.875em"
                    >
                      {{ errors[0] }}
                    </div>
                  </ValidationProvider>
                </div>
              </div>
              <div class="text-center">
                <button
                  type="submit"
                  class="login-btn btn-fill btn-info btn-wd"
                >
                  Login
                </button>
                <br />
                <div class="forgot mt-3">
                  <router-link to="/forgot-password" class="forgot-password-link">
                    Forgot your password?
                  </router-link>
                </div>
              </div>
            </card>
          </form>
        </ValidationObserver>
      </div>
    </div>

    <!-- Password Change Modal -->
    <ChangePasswordModal
      v-model="showPasswordChangeModal"
      :isForced="isPasswordChangeForced"
      @success="handlePasswordChangeSuccess"
    />
  </auth-layout>
</template>
<script>
import AuthLayout from "./AuthLayout.vue";
import ChangePasswordModal from "../components/ChangePasswordModal.vue";
import { extend } from "vee-validate";
import { required, email, min } from "vee-validate/dist/rules";
import commonHandler from "src/serviceHandlers/commonHandler";
import { useLoginStore } from "src/stores/loginStore";

const apiService = new commonHandler();

extend("email", email);
extend("required", required);
extend("min", min);

extend("password_match", {
  params: ["target"],
  validate(value, { target }) {
    return value === target;
  },
  message: "Passwords do not match",
});

export default {
  components: {
    AuthLayout,
    ChangePasswordModal,
  },
  data() {
    return {
      email: "",
      password: "",
      passwordVisible: false,
      passwordInputType: "password",
      sessionMessage: "",
      showPasswordChangeModal: false,
      isPasswordChangeForced: false,
    };
  },
  mounted() {
    this.store = useLoginStore();

    // Check for session expiration message from query params
    if (this.$route.query.message) {
      this.sessionMessage = this.$route.query.message;
      // Clear the query param after displaying
      this.$router.replace({ query: {} });
    }
  },
  methods: {
    togglePasswordVisibility() {
      switch (this.passwordInputType) {
        case "password":
          this.passwordInputType = "text";
          this.passwordVisible = true;
          break;
        case "text":
          this.passwordInputType = "password";
          this.passwordVisible = false;
          break;
      }
    },
    async signIn() {
      const login = {
        email: this.email,
        password: this.password,
      };
      this.$spinner.show();
      apiService
        .login(login, this.store, [])
        .then((response) => {
          if (response) {
            if (this.store.getLoggedIn) {
              // Check if password change is required
              if (response.requirePasswordChange) {
                this.isPasswordChangeForced = true;
                this.showPasswordChangeModal = true;
                this.$spinner.hide();
              } else {
                this.$router.push({
                  name: "Dashboard",
                });
              }
            } else if (!response.success) {
              this.$notifyError(response.message || "Incorrect username or password");
            }
          } else {
            this.$notifyError(
              "There was an error logging in. Please try again",
            );
          }
        })
        .catch((error) => {
          this.$notifyError(
            `There was an error logging in. Please try again ${error}`,
          );
        });
      this.$spinner.hide();
    },
    handlePasswordChangeSuccess() {
      this.showPasswordChangeModal = false;
      this.$notifySuccess("Password changed successfully!");
      // Navigate to dashboard after password change
      this.$router.push({
        name: "Dashboard",
      });
    },
  },
};
</script>
<style>
.card-header {
  background: none !important;
  height: 60px;
  font-family: "Playfair Display", serif;
}

.login-page .card {
  max-width: 340px !important;
  margin: 0 auto;
}
</style>
<style scoped>
.card-title {
  font-family: "Playfair Display", serif;
}

.login-btn {
  background-color: rgba(37, 55, 65, 0.8);
  padding-bottom: 7px;
  padding-top: 5px;
  border-radius: 12px;
  border: 1px solid #253741;
  color: #d9d9d9 !important;
  font-family: "Nimbus Sans Light", sans-serif;
  font-weight: bold;
  transition: all 0.3s ease;
  cursor: pointer;
}

.login-btn:hover {
  background-color: rgba(37, 55, 65, 1);
  border-color: rgba(255, 255, 255, 0.2);
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
}

label {
  color: #d9d9d9 !important;
  font-family: "Nimbus Sans Light", sans-serif;
  font-size: 14px !important;
  letter-spacing: 0.05rem !important;
}

.forgot-password-link {
  color: #d9d9d9;
  text-decoration: underline;
  font-size: 14px;
  transition: color 0.3s ease;
}

.forgot-password-link:hover {
  color: #fff;
}

.login-fields-container {
  padding: 0 15px;
}

.login-fields-container label {
  display: block !important;
  width: 100%;
}

.login-fields-container .small-select {
  width: 100% !important;
  max-width: 100% !important;
}

.login-fields-container .position-relative {
  width: 100% !important;
  display: block !important;
}

.login-fields-container .el-input {
  width: 100% !important;
}

.login-fields-container .el-input__inner {
  width: 100% !important;
}
</style>
